import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { paymentsApi } from '../services/paymentsApi';
import { 
  Shield, Tag, Plus, Trash2, Loader2, X, DollarSign, Users, CheckCircle, 
  XCircle, Copy, Eye, EyeOff, Database, Edit, UserPlus, Crown, Search,
  ChevronDown, ChevronUp, KeyRound, Mail, Send
} from 'lucide-react';

const ADMIN_EMAIL = 'reggie@coolgeek.me';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Package options for discount codes
const PACKAGE_TYPES = [
  { id: 'calendar', label: 'Pay-per-use Calendars' },
  { id: 'subscription', label: 'Subscriptions' },
  { id: 'lifetime', label: 'Lifetime Deals' },
  { id: 'addon', label: 'Add-ons' },
];

const ALL_PACKAGES = [
  // Calendars
  { id: 'calendar_7', label: '7-Day Calendar', type: 'calendar' },
  { id: 'calendar_14', label: '14-Day Calendar', type: 'calendar' },
  { id: 'calendar_30', label: '30-Day Calendar', type: 'calendar' },
  // Subscriptions
  { id: 'subscription_starter', label: 'Starter Monthly', type: 'subscription' },
  { id: 'subscription_pro', label: 'Pro Monthly', type: 'subscription' },
  { id: 'subscription_agency', label: 'Agency Monthly', type: 'subscription' },
  // Lifetime
  { id: 'ltd_starter', label: 'Lifetime Starter', type: 'lifetime' },
  { id: 'ltd_pro', label: 'Lifetime Pro', type: 'lifetime' },
  { id: 'ltd_agency', label: 'Lifetime Agency', type: 'lifetime' },
  // Add-ons
  { id: 'addon_platform', label: 'Extra Platform', type: 'addon' },
  { id: 'addon_posts', label: 'Extra Posts', type: 'addon' },
  { id: 'addon_regen_pack', label: 'Regen Pack', type: 'addon' },
];

const AdminDashboard = ({ onClose }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('codes');
  const [discountCodes, setDiscountCodes] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [users, setUsers] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [isSeeding, setIsSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Password reset state
  const [resetEmail, setResetEmail] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetStatus, setResetStatus] = useState(null);
  const [isResetting, setIsResetting] = useState(false);
  const [showResetInstructions, setShowResetInstructions] = useState(false);
  const [savedResetEmail, setSavedResetEmail] = useState('');
  const [savedResetPassword, setSavedResetPassword] = useState('');
  const [recentResets, setRecentResets] = useState([]);
  
  // New code form
  const [newCode, setNewCode] = useState({
    code: '',
    description: '',
    discount_percent: 0,
    discount_amount: 0,
    is_free: false,
    valid_for_package_types: [],
    valid_for_packages: [],
    max_uses: -1,
    expires_at: '',
  });

  // New/edit user form
  const [userForm, setUserForm] = useState({
    user_id: '',
    user_email: '',
    subscription_type: '',
    subscription_active: false,
    is_lifetime: false,
    calendars_remaining: 0,
    platforms_available: 1,
    posts_per_day_available: 1,
    profiles_available: 1,
    content_regens_remaining: 3,
    image_regens_remaining: 3,
    is_super_admin: false,
  });

  const isAdmin = user?.email === ADMIN_EMAIL;

  useEffect(() => {
    if (isAdmin) {
      checkSuperAdmin();
      loadData();
    }
  }, [isAdmin, activeTab]);

  const checkSuperAdmin = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/check-super-admin?admin_email=${user.email}`);
      const data = await response.json();
      setIsSuperAdmin(data.is_super_admin);
    } catch (error) {
      console.error('Error checking super admin:', error);
    }
  };

  const loadData = async () => {
    setIsLoading(true);
    try {
      if (activeTab === 'codes') {
        const codes = await paymentsApi.listDiscountCodes(user.email);
        setDiscountCodes(codes);
      } else if (activeTab === 'transactions') {
        const txns = await paymentsApi.listTransactions(user.email);
        setTransactions(txns);
      } else if (activeTab === 'users') {
        const response = await fetch(`${BACKEND_URL}/api/admin/users?admin_email=${user.email}`);
        const data = await response.json();
        setUsers(data);
      } else if (activeTab === 'password') {
        // Load recent password resets
        try {
          const response = await fetch(`${BACKEND_URL}/api/admin/password-resets?admin_email=${user.email}`);
          if (response.ok) {
            const data = await response.json();
            setRecentResets(data);
          }
        } catch (err) {
          console.log('No recent resets or endpoint not available');
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Generate a random temporary password
  const generateTempPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let password = '';
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password + '!';
  };

  // Handle password reset
  const handlePasswordReset = async () => {
    if (!resetEmail || !resetPassword) return;
    
    setIsResetting(true);
    setResetStatus(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          admin_email: user.email,
          user_email: resetEmail,
          temp_password: resetPassword
        })
      });
      
      if (response.ok) {
        setSavedResetEmail(resetEmail);
        setSavedResetPassword(resetPassword);
        setShowResetInstructions(true);
        setResetStatus({ type: 'success', message: 'Password reset saved! Follow the instructions below.' });
        
        // Reload recent resets
        const resetsResponse = await fetch(`${BACKEND_URL}/api/admin/password-resets?admin_email=${user.email}`);
        if (resetsResponse.ok) {
          const data = await resetsResponse.json();
          setRecentResets(data);
        }
      } else {
        const error = await response.json();
        setResetStatus({ type: 'error', message: error.detail || 'Failed to save password reset' });
      }
    } catch (error) {
      setResetStatus({ type: 'error', message: 'Network error. Please try again.' });
    } finally {
      setIsResetting(false);
    }
  };

  const handleCreateCode = async () => {
    try {
      const codeData = {
        ...newCode,
        valid_for_package_types: newCode.valid_for_package_types,
        valid_for_packages: newCode.valid_for_packages,
      };
      
      if (editingCode) {
        // Update existing code
        await fetch(`${BACKEND_URL}/api/admin/discount-codes/${editingCode}?admin_email=${user.email}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(codeData),
        });
      } else {
        // Create new code
        await paymentsApi.createDiscountCode(user.email, codeData);
      }
      
      setShowCreateModal(false);
      setEditingCode(null);
      resetCodeForm();
      loadData();
    } catch (error) {
      alert('Error saving discount code');
    }
  };

  const resetCodeForm = () => {
    setNewCode({
      code: '',
      description: '',
      discount_percent: 0,
      discount_amount: 0,
      is_free: false,
      valid_for_package_types: [],
      valid_for_packages: [],
      max_uses: -1,
      expires_at: '',
    });
  };

  const handleEditCode = (code) => {
    setNewCode({
      code: code.code,
      description: code.description || '',
      discount_percent: code.discount_percent || 0,
      discount_amount: code.discount_amount || 0,
      is_free: code.is_free || false,
      valid_for_package_types: code.valid_for_package_types || [],
      valid_for_packages: code.valid_for_packages || [],
      max_uses: code.max_uses || -1,
      expires_at: code.expires_at ? code.expires_at.split('T')[0] : '',
    });
    setEditingCode(code.code);
    setShowCreateModal(true);
  };

  const handleDeleteCode = async (code) => {
    if (!window.confirm(`Delete discount code "${code}"?`)) return;
    try {
      await paymentsApi.deleteDiscountCode(user.email, code);
      loadData();
    } catch (error) {
      alert('Error deleting code');
    }
  };

  const handleTogglePackageType = (typeId) => {
    setNewCode(prev => {
      const types = prev.valid_for_package_types.includes(typeId)
        ? prev.valid_for_package_types.filter(t => t !== typeId)
        : [...prev.valid_for_package_types, typeId];
      return { ...prev, valid_for_package_types: types };
    });
  };

  const handleTogglePackage = (pkgId) => {
    setNewCode(prev => {
      const packages = prev.valid_for_packages.includes(pkgId)
        ? prev.valid_for_packages.filter(p => p !== pkgId)
        : [...prev.valid_for_packages, pkgId];
      return { ...prev, valid_for_packages: packages };
    });
  };

  // User management functions
  const handleCreateUser = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users?admin_email=${user.email}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userForm),
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to create user');
      }
      
      setShowUserModal(false);
      resetUserForm();
      loadData();
    } catch (error) {
      alert(error.message);
    }
  };

  const handleUpdateUser = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${editingUser}?admin_email=${user.email}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userForm),
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to update user');
      }
      
      setShowUserModal(false);
      setEditingUser(null);
      resetUserForm();
      loadData();
    } catch (error) {
      alert(error.message);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Delete this user and ALL their data? This cannot be undone.')) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${userId}?admin_email=${user.email}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to delete user');
      }
      
      loadData();
    } catch (error) {
      alert(error.message);
    }
  };

  const handleEditUser = (u) => {
    setUserForm({
      user_id: u.user_id,
      user_email: u.user_email || '',
      subscription_type: u.subscription_type || '',
      subscription_active: u.subscription_active || false,
      is_lifetime: u.is_lifetime || false,
      calendars_remaining: u.calendars_remaining || 0,
      platforms_available: u.platforms_available || 1,
      posts_per_day_available: u.posts_per_day_available || 1,
      profiles_available: u.profiles_available || 1,
      content_regens_remaining: u.content_regens_remaining || 3,
      image_regens_remaining: u.image_regens_remaining || 3,
      is_super_admin: u.is_super_admin || false,
    });
    setEditingUser(u.user_id);
    setShowUserModal(true);
  };

  const resetUserForm = () => {
    setUserForm({
      user_id: '',
      user_email: '',
      subscription_type: '',
      subscription_active: false,
      is_lifetime: false,
      calendars_remaining: 0,
      platforms_available: 1,
      posts_per_day_available: 1,
      profiles_available: 1,
      content_regens_remaining: 3,
      image_regens_remaining: 3,
      is_super_admin: false,
    });
  };

  const handleSeedData = async () => {
    setIsSeeding(true);
    setSeedResult(null);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/seed?admin_email=${user.email}`, {
        method: 'POST',
      });
      const data = await response.json();
      setSeedResult(data);
      loadData();
    } catch (error) {
      setSeedResult({ error: 'Failed to seed data' });
    } finally {
      setIsSeeding(false);
    }
  };

  const filteredUsers = users.filter(u => 
    u.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.user_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.subscription_type?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isAdmin) {
    return (
      <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="bg-slate-800 border border-red-500 rounded-2xl p-8 text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white">Access Denied</h2>
          <p className="text-slate-400 mt-2">You don't have admin access.</p>
          <button onClick={onClose} className="mt-4 px-4 py-2 bg-slate-700 text-white rounded-lg">
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-600/20 rounded-lg">
              <Shield className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                Admin Dashboard
                {isSuperAdmin && (
                  <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full flex items-center gap-1">
                    <Crown className="w-3 h-3" /> Super Admin
                  </span>
                )}
              </h2>
              <p className="text-sm text-slate-400">Manage discount codes, transactions & users</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-700 px-6 shrink-0 overflow-x-auto">
          {['codes', 'transactions', 'users', 'password'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab
                  ? 'border-purple-500 text-purple-400'
                  : 'border-transparent text-slate-400 hover:text-white'
              }`}
            >
              {tab === 'codes' && <><Tag className="w-4 h-4 inline mr-2" />Discount Codes</>}
              {tab === 'transactions' && <><DollarSign className="w-4 h-4 inline mr-2" />Transactions</>}
              {tab === 'users' && <><Users className="w-4 h-4 inline mr-2" />Users</>}
              {tab === 'password' && <><KeyRound className="w-4 h-4 inline mr-2" />Password Reset</>}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
            </div>
          ) : (
            <>
              {/* Discount Codes Tab */}
              {activeTab === 'codes' && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-white">Discount Codes ({discountCodes.length})</h3>
                    <div className="flex gap-2">
                      <button
                        onClick={handleSeedData}
                        disabled={isSeeding}
                        className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 flex items-center gap-2 disabled:opacity-50"
                      >
                        {isSeeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
                        Seed Default Codes
                      </button>
                      <button
                        onClick={() => { resetCodeForm(); setEditingCode(null); setShowCreateModal(true); }}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-500 flex items-center gap-2"
                      >
                        <Plus className="w-4 h-4" /> New Code
                      </button>
                    </div>
                  </div>

                  {seedResult && (
                    <div className={`p-3 rounded-lg ${seedResult.error ? 'bg-red-900/30 text-red-400' : 'bg-green-900/30 text-green-400'}`}>
                      {seedResult.error || seedResult.message}
                    </div>
                  )}

                  <div className="space-y-2">
                    {discountCodes.map((code) => (
                      <div key={code.id} className="bg-slate-900/50 border border-slate-700 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <span className="font-mono text-lg text-purple-400 font-bold">{code.code}</span>
                            {code.is_free ? (
                              <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded">FREE</span>
                            ) : code.discount_percent > 0 ? (
                              <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded">{code.discount_percent}% OFF</span>
                            ) : (
                              <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded">${code.discount_amount} OFF</span>
                            )}
                            {!code.is_active && (
                              <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded">INACTIVE</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEditCode(code)}
                              className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteCode(code.code)}
                              className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <div className="mt-2 text-sm text-slate-400 space-y-1">
                          {code.description && <p>{code.description}</p>}
                          <div className="flex flex-wrap gap-3">
                            <span>Uses: {code.times_used}/{code.max_uses === -1 ? '∞' : code.max_uses}</span>
                            {code.expires_at && <span>Expires: {new Date(code.expires_at).toLocaleDateString()}</span>}
                          </div>
                          {(code.valid_for_package_types?.length > 0 || code.valid_for_packages?.length > 0) && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {code.valid_for_package_types?.map(t => (
                                <span key={t} className="px-1.5 py-0.5 bg-slate-700 text-xs rounded">{t}</span>
                              ))}
                              {code.valid_for_packages?.map(p => (
                                <span key={p} className="px-1.5 py-0.5 bg-indigo-900/50 text-indigo-300 text-xs rounded">{p}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Transactions Tab */}
              {activeTab === 'transactions' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-white">Recent Transactions ({transactions.length})</h3>
                  <div className="space-y-2">
                    {transactions.map((txn) => (
                      <div key={txn.id} className="bg-slate-900/50 border border-slate-700 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-white font-medium">{txn.user_email}</p>
                            <p className="text-sm text-slate-400">{txn.package_type}: {txn.package_id}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-green-400 font-bold">${txn.amount}</p>
                            <p className={`text-xs ${txn.payment_status === 'paid' ? 'text-green-400' : 'text-amber-400'}`}>
                              {txn.payment_status}
                            </p>
                          </div>
                        </div>
                        <p className="text-xs text-slate-500 mt-2">
                          {new Date(txn.created_at).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Users Tab */}
              {activeTab === 'users' && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-white">Users ({filteredUsers.length})</h3>
                    {isSuperAdmin && (
                      <button
                        onClick={() => { resetUserForm(); setEditingUser(null); setShowUserModal(true); }}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-500 flex items-center gap-2"
                      >
                        <UserPlus className="w-4 h-4" /> Add User
                      </button>
                    )}
                  </div>

                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search by email, user ID, or subscription..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full bg-slate-900 border border-slate-700 rounded-lg pl-10 pr-4 py-2 text-white placeholder-slate-500 focus:border-purple-500 outline-none"
                    />
                  </div>

                  <div className="space-y-2">
                    {filteredUsers.map((u) => (
                      <div key={u.user_id} className="bg-slate-900/50 border border-slate-700 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-white font-medium flex items-center gap-2">
                              {u.user_email}
                              {u.is_super_admin && <Crown className="w-4 h-4 text-amber-400" />}
                            </p>
                            <p className="text-xs text-slate-500 font-mono">{u.user_id}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right text-sm">
                              <p className={u.subscription_active ? 'text-green-400' : 'text-slate-400'}>
                                {u.subscription_type || 'Free'}
                              </p>
                              <p className="text-xs text-slate-500">
                                {u.calendars_remaining === -1 ? '∞' : u.calendars_remaining} calendars
                              </p>
                            </div>
                            {isSuperAdmin && (
                              <div className="flex gap-1">
                                <button
                                  onClick={() => handleEditUser(u)}
                                  className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteUser(u.user_id)}
                                  className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Password Reset Tab */}
              {activeTab === 'password' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Reset User Password</h3>
                    <p className="text-sm text-slate-400">
                      Enter the user's email and set a temporary password. You'll need to update it in Firebase Console, then share the password with the user.
                    </p>
                  </div>

                  {/* Reset Form */}
                  <div className="bg-slate-900/50 border border-slate-700 rounded-xl p-6 space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-300">User Email</label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                        <input
                          type="email"
                          value={resetEmail}
                          onChange={(e) => setResetEmail(e.target.value)}
                          placeholder="user@example.com"
                          className="w-full bg-slate-800 border border-slate-600 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-slate-500 focus:border-purple-500 outline-none"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-300">Temporary Password</label>
                      <div className="relative">
                        <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                        <input
                          type="text"
                          value={resetPassword}
                          onChange={(e) => setResetPassword(e.target.value)}
                          placeholder="TempPass123!"
                          className="w-full bg-slate-800 border border-slate-600 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-slate-500 focus:border-purple-500 outline-none"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => setResetPassword(generateTempPassword())}
                        className="text-xs text-purple-400 hover:text-purple-300"
                      >
                        Generate random password
                      </button>
                    </div>

                    {resetStatus && (
                      <div className={`p-3 rounded-lg flex items-center gap-2 text-sm ${
                        resetStatus.type === 'success' 
                          ? 'bg-green-900/30 text-green-400 border border-green-800/50' 
                          : 'bg-red-900/30 text-red-400 border border-red-800/50'
                      }`}>
                        {resetStatus.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                        {resetStatus.message}
                      </div>
                    )}

                    <button
                      onClick={handlePasswordReset}
                      disabled={isResetting || !resetEmail || !resetPassword}
                      className="w-full px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isResetting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                      Save & Get Instructions
                    </button>
                  </div>

                  {/* Instructions Box */}
                  {showResetInstructions && (
                    <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6 space-y-4">
                      <h4 className="text-amber-400 font-semibold flex items-center gap-2">
                        <CheckCircle className="w-5 h-5" /> Password Reset Instructions
                      </h4>
                      
                      <div className="space-y-3 text-sm">
                        <p className="text-slate-300"><strong>Step 1:</strong> Go to Firebase Console and update the user's password:</p>
                        <a 
                          href="https://console.firebase.google.com/project/_/authentication/users" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800 text-purple-400 hover:text-purple-300 rounded-lg border border-slate-700"
                        >
                          Open Firebase Console <span className="text-xs">↗</span>
                        </a>
                        
                        <p className="text-slate-300"><strong>Step 2:</strong> Find the user by email: <span className="text-white font-mono bg-slate-800 px-2 py-0.5 rounded">{savedResetEmail}</span></p>
                        
                        <p className="text-slate-300"><strong>Step 3:</strong> Click the three dots → "Reset password" → Enter the password below:</p>
                        
                        <div className="flex items-center gap-2">
                          <code className="flex-1 bg-slate-800 text-green-400 px-4 py-2 rounded-lg font-mono text-base">
                            {savedResetPassword}
                          </code>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(savedResetPassword);
                              setResetStatus({ type: 'success', message: 'Password copied!' });
                            }}
                            className="p-2 bg-slate-800 text-slate-400 hover:text-white rounded-lg"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                        
                        <p className="text-slate-300"><strong>Step 4:</strong> Tell the user their temporary password (via chat, phone, etc.)</p>
                        
                        <p className="text-slate-400 text-xs mt-4">
                          The user can change their password in Profile Settings after logging in.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Recent Resets */}
                  {recentResets.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium text-slate-400">Recent Password Resets</h4>
                      <div className="space-y-2">
                        {recentResets.map((reset, idx) => (
                          <div key={idx} className="bg-slate-900/30 border border-slate-700 rounded-lg p-3 flex items-center justify-between">
                            <div>
                              <p className="text-white text-sm">{reset.email}</p>
                              <p className="text-xs text-slate-500">{new Date(reset.created_at).toLocaleString()}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <code className="text-xs bg-slate-800 text-green-400 px-2 py-1 rounded font-mono">
                                {reset.temp_password}
                              </code>
                              <button
                                onClick={() => navigator.clipboard.writeText(reset.temp_password)}
                                className="p-1.5 text-slate-400 hover:text-white"
                              >
                                <Copy className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Create/Edit Discount Code Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-lg max-h-[80vh] overflow-y-auto">
            <div className="p-4 border-b border-slate-700">
              <h3 className="text-lg font-bold text-white">
                {editingCode ? 'Edit Discount Code' : 'Create Discount Code'}
              </h3>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="text-sm text-slate-400 block mb-1">Code</label>
                <input
                  type="text"
                  value={newCode.code}
                  onChange={(e) => setNewCode({...newCode, code: e.target.value.toUpperCase()})}
                  disabled={!!editingCode}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white disabled:opacity-50"
                  placeholder="SUMMER20"
                />
              </div>

              <div>
                <label className="text-sm text-slate-400 block mb-1">Description (optional)</label>
                <input
                  type="text"
                  value={newCode.description}
                  onChange={(e) => setNewCode({...newCode, description: e.target.value})}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                  placeholder="Summer promotion 20% off"
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-sm text-slate-400 block mb-1">% Off</label>
                  <input
                    type="number"
                    value={newCode.discount_percent}
                    onChange={(e) => setNewCode({...newCode, discount_percent: parseInt(e.target.value) || 0})}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                    min="0"
                    max="100"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-400 block mb-1">$ Off</label>
                  <input
                    type="number"
                    value={newCode.discount_amount}
                    onChange={(e) => setNewCode({...newCode, discount_amount: parseFloat(e.target.value) || 0})}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Max Uses</label>
                  <input
                    type="number"
                    value={newCode.max_uses}
                    onChange={(e) => setNewCode({...newCode, max_uses: parseInt(e.target.value) || -1})}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                    min="-1"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newCode.is_free}
                    onChange={(e) => setNewCode({...newCode, is_free: e.target.checked})}
                    className="w-4 h-4 rounded bg-slate-700"
                  />
                  <span className="text-sm text-white">100% Free (no payment)</span>
                </label>
              </div>

              <div>
                <label className="text-sm text-slate-400 block mb-1">Expires (optional)</label>
                <input
                  type="date"
                  value={newCode.expires_at}
                  onChange={(e) => setNewCode({...newCode, expires_at: e.target.value})}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                />
              </div>

              <div>
                <label className="text-sm text-slate-400 block mb-2">Valid for Package Types</label>
                <div className="flex flex-wrap gap-2">
                  {PACKAGE_TYPES.map((type) => (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => handleTogglePackageType(type.id)}
                      className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                        newCode.valid_for_package_types.includes(type.id)
                          ? 'bg-purple-600 border-purple-500 text-white'
                          : 'bg-slate-900 border-slate-600 text-slate-400 hover:border-slate-500'
                      }`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-1">Leave empty for all types</p>
              </div>

              <div>
                <label className="text-sm text-slate-400 block mb-2">Valid for Specific Packages</label>
                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                  {ALL_PACKAGES.map((pkg) => (
                    <button
                      key={pkg.id}
                      type="button"
                      onClick={() => handleTogglePackage(pkg.id)}
                      className={`px-2 py-1 text-xs rounded border transition-colors ${
                        newCode.valid_for_packages.includes(pkg.id)
                          ? 'bg-indigo-600 border-indigo-500 text-white'
                          : 'bg-slate-900 border-slate-600 text-slate-400 hover:border-slate-500'
                      }`}
                    >
                      {pkg.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-1">Leave empty for all packages</p>
              </div>
            </div>
            <div className="p-4 border-t border-slate-700 flex justify-end gap-3">
              <button
                onClick={() => { setShowCreateModal(false); setEditingCode(null); }}
                className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateCode}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-500"
              >
                {editingCode ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit User Modal */}
      {showUserModal && isSuperAdmin && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-lg max-h-[80vh] overflow-y-auto">
            <div className="p-4 border-b border-slate-700">
              <h3 className="text-lg font-bold text-white">
                {editingUser ? 'Edit User' : 'Create User'}
              </h3>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-slate-400 block mb-1">User ID</label>
                  <input
                    type="text"
                    value={userForm.user_id}
                    onChange={(e) => setUserForm({...userForm, user_id: e.target.value})}
                    disabled={!!editingUser}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm disabled:opacity-50"
                    placeholder="Firebase UID"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Email</label>
                  <input
                    type="email"
                    value={userForm.user_email}
                    onChange={(e) => setUserForm({...userForm, user_email: e.target.value})}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
                    placeholder="user@example.com"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm text-slate-400 block mb-1">Subscription Type</label>
                <select
                  value={userForm.subscription_type}
                  onChange={(e) => setUserForm({...userForm, subscription_type: e.target.value})}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                >
                  <option value="">None (Free)</option>
                  <option value="subscription_starter">Starter Monthly</option>
                  <option value="subscription_pro">Pro Monthly</option>
                  <option value="subscription_agency">Agency Monthly</option>
                  <option value="ltd_starter">Lifetime Starter</option>
                  <option value="ltd_pro">Lifetime Pro</option>
                  <option value="ltd_agency">Lifetime Agency</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>

              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={userForm.subscription_active}
                    onChange={(e) => setUserForm({...userForm, subscription_active: e.target.checked})}
                    className="w-4 h-4 rounded bg-slate-700"
                  />
                  <span className="text-sm text-white">Subscription Active</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={userForm.is_lifetime}
                    onChange={(e) => setUserForm({...userForm, is_lifetime: e.target.checked})}
                    className="w-4 h-4 rounded bg-slate-700"
                  />
                  <span className="text-sm text-white">Lifetime Deal</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={userForm.is_super_admin}
                    onChange={(e) => setUserForm({...userForm, is_super_admin: e.target.checked})}
                    className="w-4 h-4 rounded bg-slate-700"
                  />
                  <span className="text-sm text-amber-400 flex items-center gap-1">
                    <Crown className="w-3 h-3" /> Super Admin
                  </span>
                </label>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Calendars</label>
                  <input
                    type="number"
                    value={userForm.calendars_remaining}
                    onChange={(e) => setUserForm({...userForm, calendars_remaining: parseInt(e.target.value) || 0})}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                    min="-1"
                  />
                  <p className="text-xs text-slate-500">-1 = unlimited</p>
                </div>
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Platforms</label>
                  <input
                    type="number"
                    value={userForm.platforms_available}
                    onChange={(e) => setUserForm({...userForm, platforms_available: parseInt(e.target.value) || 1})}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                    min="1"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Posts/Day</label>
                  <input
                    type="number"
                    value={userForm.posts_per_day_available}
                    onChange={(e) => setUserForm({...userForm, posts_per_day_available: parseInt(e.target.value) || 1})}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                    min="1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Profiles</label>
                  <input
                    type="number"
                    value={userForm.profiles_available}
                    onChange={(e) => setUserForm({...userForm, profiles_available: parseInt(e.target.value) || 1})}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                    min="1"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Content Regens</label>
                  <input
                    type="number"
                    value={userForm.content_regens_remaining}
                    onChange={(e) => setUserForm({...userForm, content_regens_remaining: parseInt(e.target.value) || 0})}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                    min="0"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Image Regens</label>
                  <input
                    type="number"
                    value={userForm.image_regens_remaining}
                    onChange={(e) => setUserForm({...userForm, image_regens_remaining: parseInt(e.target.value) || 0})}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                    min="0"
                  />
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-slate-700 flex justify-end gap-3">
              <button
                onClick={() => { setShowUserModal(false); setEditingUser(null); }}
                className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600"
              >
                Cancel
              </button>
              <button
                onClick={editingUser ? handleUpdateUser : handleCreateUser}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-500"
              >
                {editingUser ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
